import { type Context, Controller } from '@hotwired/stimulus';
import type { EmojiClickEvent } from 'emoji-picker-element/shared';
import { createElement } from 'src/utils/dom-create';
import { toggleHidden } from 'src/utils/dom-toggle';

export default class EmojiPickerController extends Controller {
  static targets = ['input', 'toggleTrigger', 'clearTrigger', 'clearTarget'];

  declare private inputTarget: HTMLInputElement;
  declare private toggleTriggerTarget: HTMLButtonElement;
  declare private clearTriggerTarget: HTMLButtonElement;
  declare private clearTargetTargets: HTMLElement[];

  private picker: HTMLElement | undefined;
  private connected: boolean = false;

  constructor(context: Context) {
    super(context);

    this.onEmojiPicked = this.onEmojiPicked.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onClear = this.onClear.bind(this);
  }

  connect() {
    this.connected = true;

    import('emoji-picker-element')
      .then(
        () =>
          this.connected &&
          this.toggleTriggerTarget.removeAttribute('disabled'),
      )
      .catch(() => {});

    this.element.addEventListener('emoji-click', this.onEmojiPicked);
    this.toggleTriggerTarget.addEventListener('click', this.onClick);
    this.clearTriggerTarget.addEventListener('click', this.onClear);

    toggleHidden(this.clearTriggerTarget, !this.inputTarget.value);
  }

  disconnect(): void {
    this.connected = false;

    if (this.picker) {
      this.picker.remove();
      this.picker = undefined;
    }

    this.element.removeEventListener('emoji-click', this.onEmojiPicked);
    this.toggleTriggerTarget.removeEventListener('click', this.onClick);
    this.clearTriggerTarget.removeEventListener('click', this.onClear);

    toggleHidden(this.clearTriggerTarget, true);
  }

  public onEmojiPicked(event: Event) {
    if (!('detail' in event)) {
      return;
    }

    const customEvent = event as EmojiClickEvent;
    const emoji = customEvent.detail.unicode ?? null;

    this.inputTarget.value = emoji ?? '';
    this.toggleTriggerTarget.textContent = emoji ?? 'ℹ️';

    this.picker?.remove();
    this.picker = undefined;

    toggleHidden(this.clearTriggerTarget, !this.inputTarget.value);

    this.toggleTriggerTarget.focus();
  }

  public onClick(_event: Event) {
    if (this.picker) {
      this.picker?.remove();
      this.picker = undefined;
      return;
    }

    this.picker = createElement('emoji-picker', {});
    this.element.append(this.picker);
  }

  public onClear(_event: Event) {
    if (this.picker) {
      this.picker?.remove();
      this.picker = undefined;
      return;
    }

    this.inputTarget.value = '';
    this.toggleTriggerTarget.textContent = 'ℹ️';

    this.clearTargetTargets.forEach((target) => {
      if ('value' in target) {
        target.value = '';
      } else {
        target.textContent = '';
      }
    });

    toggleHidden(this.clearTriggerTarget, !this.inputTarget.value);
  }
}
